import { SlideObject } from '../types';
import { ResizeHandle, rotatedAabb, snapValue } from './objectTransform';

export const GUIDE_THRESHOLD_PX = 6;

export type GuideBox = { x: number; y: number; w: number; h: number };

export type SmartGuideLine = {
  kind: 'align' | 'spacing';
  axis: 'x' | 'y';
  position: number;
  start?: number;
  end?: number;
  cross?: number;
};

export type EdgeLock = {
  left?: boolean;
  right?: boolean;
  top?: boolean;
  bottom?: boolean;
};

export type SmartGuideResult = {
  dx: number;
  dy: number;
  xEdge?: AxisEdge;
  yEdge?: AxisEdge;
  lines: SmartGuideLine[];
};

export type AxisEdge = 'start' | 'center' | 'end';

type AxisCandidate = {
  axis: 'x' | 'y';
  delta: number;
  edge: AxisEdge;
  lines: SmartGuideLine[];
};

export function objectGuideBox(object: Pick<SlideObject, 'x' | 'y' | 'w' | 'h' | 'style'>): GuideBox {
  return rotatedAabb(object.x, object.y, object.w, object.h, object.style?.rotation || 0);
}

export function unionGuideBoxes(boxes: GuideBox[]): GuideBox {
  const left = Math.min(...boxes.map((box) => box.x));
  const top = Math.min(...boxes.map((box) => box.y));
  const right = Math.max(...boxes.map((box) => box.x + box.w));
  const bottom = Math.max(...boxes.map((box) => box.y + box.h));
  return { x: left, y: top, w: Math.max(1, right - left), h: Math.max(1, bottom - top) };
}

export function resizeEdgeLocks(handle?: ResizeHandle): EdgeLock {
  if (!handle) return {};
  return {
    left: !handle.includes('w'),
    right: !handle.includes('e'),
    top: !handle.includes('n'),
    bottom: !handle.includes('s'),
  };
}

function overlapRange(a0: number, a1: number, b0: number, b1: number): { start: number; end: number } | null {
  const start = Math.max(a0, b0);
  const end = Math.min(a1, b1);
  if (end <= start) return null;
  return { start, end };
}

function overlapMid(a0: number, a1: number, b0: number, b1: number): number | null {
  const range = overlapRange(a0, a1, b0, b1);
  return range ? (range.start + range.end) / 2 : null;
}

function uniquePositions(values: number[]): number[] {
  const seen = new Set<number>();
  const out: number[] = [];
  values.forEach((value) => {
    if (seen.has(value)) return;
    seen.add(value);
    out.push(value);
  });
  return out;
}

function alignmentCandidates(
  mover: GuideBox,
  targets: GuideBox[],
  pageSize: { width: number; height: number },
  threshold: number,
  lock: EdgeLock,
  axes: { x: boolean; y: boolean },
): AxisCandidate[] {
  const out: AxisCandidate[] = [];
  const addAxis = (
    axis: 'x' | 'y',
    stops: { edge: AxisEdge; pos: number }[],
    targetPositions: number[],
  ) => {
    stops.forEach((stop) => {
      targetPositions.forEach((position) => {
        const delta = position - stop.pos;
        if (Math.abs(delta) > threshold) return;
        out.push({
          axis,
          delta,
          edge: stop.edge,
          lines: [{ kind: 'align', axis, position }],
        });
      });
    });
  };

  if (axes.x && (!lock.left || !lock.right)) {
    const stops: { edge: AxisEdge; pos: number }[] = [];
    if (!lock.left) stops.push({ edge: 'start', pos: mover.x });
    if (!lock.right) stops.push({ edge: 'end', pos: mover.x + mover.w });
    stops.push({ edge: 'center', pos: mover.x + mover.w / 2 });
    addAxis('x', stops, uniquePositions([
      0,
      pageSize.width / 2,
      pageSize.width,
      ...targets.flatMap((target) => [target.x, target.x + target.w / 2, target.x + target.w]),
    ]));
  }

  if (axes.y && (!lock.top || !lock.bottom)) {
    const stops: { edge: AxisEdge; pos: number }[] = [];
    if (!lock.top) stops.push({ edge: 'start', pos: mover.y });
    if (!lock.bottom) stops.push({ edge: 'end', pos: mover.y + mover.h });
    stops.push({ edge: 'center', pos: mover.y + mover.h / 2 });
    addAxis('y', stops, uniquePositions([
      0,
      pageSize.height / 2,
      pageSize.height,
      ...targets.flatMap((target) => [target.y, target.y + target.h / 2, target.y + target.h]),
    ]));
  }

  return out;
}

function spacingCandidates(
  mover: GuideBox,
  targets: GuideBox[],
  threshold: number,
  axes: { x: boolean; y: boolean },
): AxisCandidate[] {
  const out: AxisCandidate[] = [];
  const run = (axis: 'x' | 'y') => {
    const startOf = (box: GuideBox) => (axis === 'x' ? box.x : box.y);
    const endOf = (box: GuideBox) => (axis === 'x' ? box.x + box.w : box.y + box.h);
    const cross0 = (box: GuideBox) => (axis === 'x' ? box.y : box.x);
    const cross1 = (box: GuideBox) => (axis === 'x' ? box.y + box.h : box.x + box.w);
    const overlapping = targets.filter((target) => overlapMid(cross0(mover), cross1(mover), cross0(target), cross1(target)) != null);
    if (overlapping.length < 2) return;

    const sorted = [...overlapping].sort((left, right) => startOf(left) - startOf(right));
    const refGaps: { size: number; a: GuideBox; b: GuideBox }[] = [];
    for (let index = 0; index < sorted.length - 1; index += 1) {
      const size = startOf(sorted[index + 1]) - endOf(sorted[index]);
      if (size > 0) refGaps.push({ size, a: sorted[index], b: sorted[index + 1] });
    }

    const moverStart = startOf(mover);
    const moverSize = endOf(mover) - startOf(mover);
    const push = (desiredStart: number, lines: SmartGuideLine[]) => {
      const delta = desiredStart - moverStart;
      if (Math.abs(delta) > threshold) return;
      out.push({ axis, delta, edge: 'start', lines });
    };

    for (let index = 0; index < sorted.length - 1; index += 1) {
      const left = sorted[index];
      const right = sorted[index + 1];
      const space = startOf(right) - endOf(left);
      if (space <= moverSize) continue;
      const desired = endOf(left) + (space - moverSize) / 2;
      const cross = overlapMid(cross0(mover), cross1(mover), cross0(left), cross1(left))
        ?? overlapMid(cross0(mover), cross1(mover), cross0(right), cross1(right))
        ?? (cross0(mover) + cross1(mover)) / 2;
      push(desired, [
        { kind: 'spacing', axis, position: desired, start: endOf(left), end: desired, cross },
        { kind: 'spacing', axis, position: desired + moverSize, start: desired + moverSize, end: startOf(right), cross },
      ]);
    }

    overlapping.forEach((neighbor) => {
      const cross = overlapMid(cross0(mover), cross1(mover), cross0(neighbor), cross1(neighbor));
      if (cross == null) return;
      refGaps.forEach((gap) => {
        const refCross = overlapMid(cross0(gap.a), cross1(gap.a), cross0(gap.b), cross1(gap.b)) ?? cross;
        const refLine: SmartGuideLine = {
          kind: 'spacing',
          axis,
          position: endOf(gap.a),
          start: endOf(gap.a),
          end: startOf(gap.b),
          cross: refCross,
        };
        const leftStart = startOf(neighbor) - gap.size - moverSize;
        push(leftStart, [
          {
            kind: 'spacing',
            axis,
            position: leftStart + moverSize,
            start: leftStart + moverSize,
            end: startOf(neighbor),
            cross,
          },
          refLine,
        ]);
        const rightStart = endOf(neighbor) + gap.size;
        push(rightStart, [
          {
            kind: 'spacing',
            axis,
            position: endOf(neighbor),
            start: endOf(neighbor),
            end: rightStart,
            cross,
          },
          refLine,
        ]);
      });
    });
  };

  if (axes.x) run('x');
  if (axes.y) run('y');
  return out;
}

function pickWinners(candidates: AxisCandidate[]): SmartGuideResult {
  const bestFor = (axis: 'x' | 'y'): { delta: number; edge: AxisEdge; lines: SmartGuideLine[] } | null => {
    const list = candidates.filter((candidate) => candidate.axis === axis);
    if (!list.length) return null;
    let best = list[0];
    list.forEach((candidate) => {
      if (Math.abs(candidate.delta) < Math.abs(best.delta) - 1e-6) best = candidate;
    });
    const lines: SmartGuideLine[] = [];
    const seen = new Set<string>();
    list.filter((candidate) => Math.abs(candidate.delta - best.delta) < 1e-6).forEach((candidate) => {
      candidate.lines.forEach((line) => {
        const key = line.kind === 'align'
          ? `a:${line.axis}:${line.position}`
          : `s:${line.axis}:${line.start}:${line.end}:${line.cross}`;
        if (seen.has(key)) return;
        seen.add(key);
        lines.push(line);
      });
    });
    return { delta: best.delta, edge: best.edge, lines };
  };

  const x = bestFor('x');
  const y = bestFor('y');
  return {
    dx: x?.delta ?? 0,
    dy: y?.delta ?? 0,
    xEdge: x?.edge,
    yEdge: y?.edge,
    lines: [...(x?.lines ?? []), ...(y?.lines ?? [])],
  };
}

export function resolveSmartGuides(input: {
  mover: GuideBox;
  targets: GuideBox[];
  pageSize: { width: number; height: number };
  threshold?: number;
  lock?: EdgeLock;
  axes?: { x?: boolean; y?: boolean };
  includeSpacing?: boolean;
}): SmartGuideResult {
  const threshold = input.threshold ?? GUIDE_THRESHOLD_PX;
  const lock = input.lock || {};
  const axes = { x: input.axes?.x !== false, y: input.axes?.y !== false };
  const candidates = alignmentCandidates(input.mover, input.targets, input.pageSize, threshold, lock, axes);
  if (input.includeSpacing) {
    candidates.push(...spacingCandidates(input.mover, input.targets, threshold, axes));
  }
  return pickWinners(candidates);
}

function applyLockedDelta(box: GuideBox, axis: 'x' | 'y', delta: number, edge: AxisEdge, lock: EdgeLock, minSize: number): GuideBox {
  if (axis === 'x') {
    if (!lock.left && !lock.right) return { ...box, x: box.x + delta };
    const grow = edge === 'center' ? delta * 2 : delta;
    if (lock.left && !lock.right) return { ...box, w: Math.max(minSize, box.w + grow) };
    if (!lock.left && lock.right) {
      const nextW = Math.max(minSize, box.w - grow);
      return { ...box, x: box.x + box.w - nextW, w: nextW };
    }
    return box;
  }
  if (!lock.top && !lock.bottom) return { ...box, y: box.y + delta };
  const grow = edge === 'center' ? delta * 2 : delta;
  if (lock.top && !lock.bottom) return { ...box, h: Math.max(minSize, box.h + grow) };
  if (!lock.top && lock.bottom) {
    const nextH = Math.max(minSize, box.h - grow);
    return { ...box, y: box.y + box.h - nextH, h: nextH };
  }
  return box;
}

export function snapMoveWithGuides(input: {
  rawX: number;
  rawY: number;
  origX: number;
  origY: number;
  moving: SlideObject[];
  targets: SlideObject[];
  pageSize?: { width: number; height: number };
  snapGrid: boolean;
  enabled: boolean;
  axes?: { x?: boolean; y?: boolean };
}): { x: number; y: number; lines: SmartGuideLine[] } {
  if (!input.enabled || !input.pageSize || !input.moving.length) {
    return { x: snapValue(input.rawX, input.snapGrid), y: snapValue(input.rawY, input.snapGrid), lines: [] };
  }
  const deltaX = input.rawX - input.origX;
  const deltaY = input.rawY - input.origY;
  const mover = unionGuideBoxes(input.moving.map((object) => objectGuideBox({
    ...object,
    x: object.x + deltaX,
    y: object.y + deltaY,
  })));
  const resolved = resolveSmartGuides({
    mover,
    targets: input.targets.map(objectGuideBox),
    pageSize: input.pageSize,
    axes: input.axes,
    includeSpacing: true,
  });
  const xHit = resolved.lines.some((line) => line.axis === 'x');
  const yHit = resolved.lines.some((line) => line.axis === 'y');
  return {
    x: xHit ? input.rawX + resolved.dx : snapValue(input.rawX, input.snapGrid),
    y: yHit ? input.rawY + resolved.dy : snapValue(input.rawY, input.snapGrid),
    lines: resolved.lines,
  };
}

export function snapResizeWithGuides(input: {
  box: GuideBox;
  targets: SlideObject[];
  pageSize?: { width: number; height: number };
  snapGrid: boolean;
  enabled: boolean;
  handle?: ResizeHandle;
  rotation?: number;
  minSize?: number;
}): { box: GuideBox; lines: SmartGuideLine[] } {
  const minSize = input.minSize ?? 40;
  if (!input.enabled || !input.pageSize) {
    return {
      box: {
        x: snapValue(input.box.x, input.snapGrid),
        y: snapValue(input.box.y, input.snapGrid),
        w: snapValue(input.box.w, input.snapGrid),
        h: snapValue(input.box.h, input.snapGrid),
      },
      lines: [],
    };
  }
  const lock = resizeEdgeLocks(input.handle);
  const mover = rotatedAabb(input.box.x, input.box.y, input.box.w, input.box.h, input.rotation || 0);
  const resolved = resolveSmartGuides({
    mover,
    targets: input.targets.map(objectGuideBox),
    pageSize: input.pageSize,
    lock,
    includeSpacing: false,
  });
  let box = input.box;
  if (resolved.lines.some((line) => line.axis === 'x')) {
    box = applyLockedDelta(box, 'x', resolved.dx, resolved.xEdge || 'end', lock, minSize);
  } else if (input.snapGrid) {
    box = { ...box, x: snapValue(box.x, true), w: snapValue(box.w, true) };
  }
  if (resolved.lines.some((line) => line.axis === 'y')) {
    box = applyLockedDelta(box, 'y', resolved.dy, resolved.yEdge || 'end', lock, minSize);
  } else if (input.snapGrid) {
    box = { ...box, y: snapValue(box.y, true), h: snapValue(box.h, true) };
  }
  return { box, lines: resolved.lines };
}
